import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import StudentLayout from "../../layouts/StudentLayout";
import StudentHeaderSection from "../../components/student/StudentHeaderSection";
import EnrolledCourseCard from "../../components/student/EnrolledCourseCard";
import CoursesFilter from "../../components/student/CoursesFilter";
import ExploreCourseCard from "../../components/student/ExploreCourseCard";
import CoursePagination from "../../components/student/CoursePagination";
import Button from "../../components/common/Button";
import AIAssistantChatModal from "../../components/common/AIAssistantChatModal";
import { AssistantIcon } from "../../components/Icons";
import {
  useGetAllCoursesUserSideQuery,
  useGetUserEnrollmentsQuery,
} from "../../services/course";
import { useNavigate } from "react-router-dom";

const defaultFilters = {
  category: "all",
  type: "all",
  ageGroup: "all",
  level: "all",
};

const SEARCH_DEBOUNCE_MS = 500;

const Courses = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [enrolledSearchTerm, setEnrolledSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  const courseQueryParams = useMemo(
    () => ({
      page: currentPage,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(filters.category !== "all" && { category: filters.category }),
      ...(filters.ageGroup !== "all" && { ageGroup: filters.ageGroup }),
      ...(filters.level !== "all" && { level: filters.level }),
      ...(filters.type !== "all" && { courseEnrollementType: filters.type }),
    }),
    [currentPage, debouncedSearch, filters],
  );

  const {
    data: enrollmentsData,
    isLoading: enrollmentsLoading,
    error: enrollmentsError,
  } = useGetUserEnrollmentsQuery();
  const enrollmentsEmptyMessage =
    (enrollmentsData?.success === false && enrollmentsData?.message) ||
    (enrollmentsError?.data?.success === false &&
      enrollmentsError?.data?.message) ||
    "";

  const hasNoEnrollments =
    typeof enrollmentsEmptyMessage === "string" &&
    enrollmentsEmptyMessage.toLowerCase().includes("no approved enrollments");

  const enrolledCourses = useMemo(
    () =>
      Array.isArray(enrollmentsData?.data)
        ? enrollmentsData.data.filter(
            (item) => item && (item.enrollmentId || item.course),
          )
        : [],
    [enrollmentsData?.data],
  );

  const filteredEnrolledCourses = useMemo(() => {
    const query = enrolledSearchTerm.trim().toLowerCase();
    if (!query) return enrolledCourses;

    return enrolledCourses.filter((item) => {
      const title = item?.course?.courseTitle?.toLowerCase() || "";
      const enrollmentType = (item?.enrollmentType || "").toLowerCase();
      const category = (item?.course?.courseCategory || "").toLowerCase();

      return (
        title.includes(query) ||
        enrollmentType.includes(query) ||
        category.includes(query)
      );
    });
  }, [enrolledCourses, enrolledSearchTerm]);

  const {
    data: coursesData,
    isLoading: coursesLoading,
    error: coursesError,
  } = useGetAllCoursesUserSideQuery(courseQueryParams);

  const exploreCourses =
    coursesData?.data?.map((course) => ({
      id: course._id,
      title: course.courseTitle,
      image: course.courseThumbnail?.secureUrl,
      purchaseType:
        course.courseEnrollementType === "live"
          ? "Live Sessions"
          : "Recorded Lectures",
      price: course.coursePrice,
    })) || [];

  const totalPages = coursesData?.pagination?.totalPages || 1;

  const handlePageChange = (page) => {
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(nextPage);
  };

  return (
    <StudentLayout>
      <div className="hidden sm:block">
        <StudentHeaderSection />
      </div>
      <div className="pt-10 px-5 lg:px-12 2xl:px-20 pb-28">
        <div className=" flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8 lg:gap-16">
          <button
            type="button"
            className="w-fit px-2.5 py-2 text-left text-base font-semibold leading-7 tracking-[-1%] text-[#FF6636] sm:text-lg sm:leading-8"
          >
            Enrolled Courses
          </button>
          <button
            type="button"
            onClick={() => navigate("/student/upcoming-sessions")}
            className="w-fit px-2.5 py-2 text-left text-base font-semibold leading-7 tracking-[-1%] text-[#070200] sm:text-lg sm:leading-8"
          >
            View Upcoming Sessions
          </button>
        </div>

        <div className="max-w-[608px] mt-4">
          <p className="mb-2.5 text-[15px] leading-[18px] text-[#6B7280]">
            Search
          </p>
          <div className="flex h-8 sm:h-12 items-center gap-2 sm:gap-3 border border-[#E9EAF0] bg-white px-3 sm:px-4">
            <Search className="shrink-0 text-[#8C8CA1] size-4 sm:size-6" />
            <input
              type="text"
              value={enrolledSearchTerm}
              onChange={(event) => setEnrolledSearchTerm(event.target.value)}
              placeholder="Search in your courses..."
              className="w-full bg-transparent text-sm sm:text-base font-['Inter'] placeholder:text-[#8C8CA1] text-black outline-none  placeholder:opacity-80"
            />
          </div>
        </div>

        {enrollmentsLoading ? (
          <div className="mt-6 text-center text-gray-500">
            Loading enrolled courses...
          </div>
        ) : hasNoEnrollments ||
          (!enrollmentsError && enrolledCourses.length === 0) ? (
          <div className="mt-6 text-center text-gray-500">
            {enrollmentsEmptyMessage || "No approved enrollments found"}
          </div>
        ) : enrollmentsError ? (
          <div className="mt-6 text-center text-red-500">
            {enrollmentsError?.data?.message ||
              "Error loading enrolled courses"}
          </div>
        ) : filteredEnrolledCourses.length === 0 ? (
          <div className="mt-6 text-center text-gray-500">
            No enrolled courses match your search.
          </div>
        ) : (
          <div className="mt-6 sm:mt-11 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
            {filteredEnrolledCourses.map((course) => (
              <EnrolledCourseCard
                key={course.enrollmentId || course.id || course.title}
                course={course}
              />
            ))}
          </div>
        )}

        <h2 className="mt-10 text-xl font-semibold tracking-[-1%] text-[#1D2026] sm:mt-14 sm:text-2xl">
          Explore Courses
        </h2>

        <CoursesFilter
          filters={filters}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFiltersChange={setFilters}
        />

        <div className="mt-10 grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
          {coursesLoading ? (
            <p className="col-span-full text-center text-[#6B7280]">
              Loading courses...
            </p>
          ) : coursesError ? (
            <p className="col-span-full text-center text-[#6B7280]">
              No courses found.
            </p>
          ) : (
            exploreCourses.map((course) => (
              <ExploreCourseCard
                key={course.id || course.title}
                course={course}
              />
            ))
          )}
        </div>

        <CoursePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <div className="fixed right-5 bottom-6 z-50 lg:right-16 lg:bottom-8">
        <Button
          btnText="Talk with Us"
          btnClassName="text-white bg-[#FF6636] text-sm leading-3 lg:leading-[16.8px] rounded-xl sm:rounded-2xl py-1 sm:py-2 lg:py-[10px] px-3 sm:px-4 sm:font-medium"
          icon={AssistantIcon}
          onClick={() => setIsChatOpen(true)}
        />
      </div>

      <AIAssistantChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
    </StudentLayout>
  );
};

export default Courses;
